/**
 * LoyaltyLadderSection — "Stay with us, get cheaper" reward strip
 * with a football that rolls from "Today" up to Year 10 as the section
 * enters the viewport, revealing each discount % as the ball arrives.
 *
 * Renders nothing unless `content.loyalty_ladder.enabled === true`.
 *
 * CMS knobs (all optional, defaults baked in):
 *   loyalty_ladder.enabled              — show / hide the section
 *   loyalty_ladder.badge_text           — gold pill copy
 *   loyalty_ladder.headline             — tagline right of the pill
 *   loyalty_ladder.milestones[5]        — { year_label, discount_label }
 *   loyalty_ladder.footer_pitch_*       — intro / emphasis / outro
 *   loyalty_ladder.animate_on_scroll    — bool (default true)
 *
 * The visitor's current position ("Today") is a fixed virtual first rung
 * (not editable in CMS) so the ball has an origin to kick off from.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { trackLoyaltyLadderInteraction } from '../../utils/analytics';
import './LoyaltyLadder.css';

const DEFAULT_BALL_URL = 'https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/jyfl98do_Untitled%20design%20-%202026-06-12T185108.908.png';

const DEFAULT_MILESTONES = [
  { year_label: 'Year 1',  discount_label: '5%'  },
  { year_label: 'Year 3',  discount_label: '15%' },
  { year_label: 'Year 5',  discount_label: '25%' },
  { year_label: 'Year 7',  discount_label: '35%' },
  { year_label: 'Year 10', discount_label: '50%' },
];

const DEFAULT_COPY = {
  badge_text: 'Loyalty Ladder',
  headline: 'The longer you coach with us, the cheaper we get.',
  footer_pitch_intro: 'Most apps charge loyal users more.',
  footer_pitch_emphasis: 'We do the opposite.',
  footer_pitch_outro: 'Your renewal price drops every contract anniversary \u2014 for life.',
};

// Total animation runtime (must match keyframe durations in LoyaltyLadder.css).
const TOTAL_DURATION_S = 3.6;
// Fire the discount reveal + starburst a hair BEFORE the ball actually
// arrives so text and ball peak together (compensates for the reveal's
// own animation length).
const REVEAL_LEAD_S = 0.14;
// Pause after entering the viewport before kicking off — gives the
// visitor half a second to register the section before motion starts.
const START_DELAY_MS = 500;

const stepDelayS = (idx, count) => {
  if (count <= 1) return 0;
  return Math.max(0, (idx / (count - 1)) * TOTAL_DURATION_S - REVEAL_LEAD_S);
};

export function LoyaltyLadderSection({ content, theme, todayDiscountPct }) {
  const cfg = content?.loyalty_ladder || {};
  const isDark = theme === 'dark' || theme === 'black';
  const animateOnScroll = cfg.animate_on_scroll !== false; // default true
  const showTodayDiscount = cfg.show_today_discount !== false; // default true

  // Off by default — existing installs stay untouched until admin flips it.
  const disabled = cfg.enabled !== true;

  const copy = useMemo(() => ({ ...DEFAULT_COPY, ...cfg }), [cfg]);
  const milestones = useMemo(() => {
    const list = Array.isArray(cfg.milestones) && cfg.milestones.length > 0
      ? cfg.milestones
      : DEFAULT_MILESTONES;
    return list.slice(0, 5);
  }, [cfg.milestones]);

  // Prepend a virtual "Today" rung so the ball has an origin. Not
  // CMS-editable — it's presentational, not conceptual. The Today rung
  // mirrors the live Pricing Slider savings % so visitors see their
  // day-1 discount before the multi-year loyalty progression starts.
  // Admin can hide the discount % via `show_today_discount` (default on)
  // to A/B test whether the number helps or clutters.
  const todayLabel = (showTodayDiscount && typeof todayDiscountPct === 'number' && todayDiscountPct > 0)
    ? `${todayDiscountPct}%`
    : 'Save';
  const rungs = useMemo(() => ([
    { year_label: 'Today', discount_label: todayLabel, isToday: true, isPeak: false },
    ...milestones.map((m, i) => ({
      year_label: m.year_label,
      discount_label: m.discount_label,
      isToday: false,
      isPeak: i === milestones.length - 1,
    })),
  ]), [milestones, todayLabel]);

  const ballUrl = content?.pricing?.slider_ball_url || DEFAULT_BALL_URL;

  // Idle → animating → done state machine. When `animate_on_scroll` is off
  // we skip straight to `done` (renders the final static state).
  const containerRef = useRef(null);
  const [phase, setPhase] = useState(animateOnScroll ? 'idle' : 'done');

  // De-dup analytics: fire each (rung × interaction-type) pair at most
  // once per page load so visitors who repeatedly mouse over the rungs
  // don't drown the signal.
  const trackedInteractionsRef = useRef(new Set());
  const emitInteraction = (rungLabel, type) => {
    const key = `${type}:${rungLabel}`;
    if (trackedInteractionsRef.current.has(key)) return;
    trackedInteractionsRef.current.add(key);
    trackLoyaltyLadderInteraction(rungLabel, type);
  };

  // Trigger animation on scroll-in (once per page load).
  useEffect(() => {
    if (!animateOnScroll) return;
    const el = containerRef.current;
    if (!el || phase !== 'idle') return;
    let startTimer = null;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio > 0.4) {
          io.disconnect();
          startTimer = setTimeout(() => setPhase('animating'), START_DELAY_MS);
          break;
        }
      }
    }, { threshold: [0, 0.4, 0.6] });
    io.observe(el);
    return () => {
      io.disconnect();
      if (startTimer) clearTimeout(startTimer);
    };
  }, [animateOnScroll, phase]);

  // Flip animating → done once the sequence finishes so we don't tie
  // resources up forever.
  useEffect(() => {
    if (phase !== 'animating') return;
    const t = setTimeout(() => setPhase('done'), TOTAL_DURATION_S * 1000 + 100);
    return () => clearTimeout(t);
  }, [phase]);

  if (disabled) return null;

  const stateClass =
    phase === 'animating' ? 'll-animating'
    : phase === 'done'    ? 'll-static'
    : ''; // idle — ball parked on Today, nothing else revealed yet

  // Mobile skips the middle CMS milestone (rung index 3 in the full 6-item
  // array = idx 2 of the CMS milestones = Year 5 in defaults) so the layout
  // reflows to 5 evenly-spaced dots that match the mobile geometry vars.
  const hideMiddleIdx = 3;

  return (
    <section
      id="loyalty-ladder"
      data-section="loyalty_ladder"
      data-testid="loyalty-ladder-section"
      className="pt-0 pb-10 px-4 sm:px-6 lg:px-8 -mt-14 sm:-mt-16"
    >
      <div className="max-w-4xl mx-auto">
        <div
          ref={containerRef}
          className={`ll-container rounded-2xl border p-5 sm:p-6 relative overflow-hidden transition-colors ${stateClass} ${
            isDark
              ? 'bg-gray-900/60 border-gray-800'
              : 'bg-white border-gray-200 shadow-xl shadow-gray-200/50'
          }`}
          data-testid="loyalty-ladder-card"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />

          {/* ─── MOBILE HEADER — stacked ─── */}
          <div className="sm:hidden mb-4 space-y-2 relative">
            {copy.badge_text && (
              <span
                className="block w-full text-center px-4 py-3 rounded-2xl text-sm font-black uppercase tracking-widest bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-md"
                data-testid="loyalty-ladder-badge-mobile"
              >
                {copy.badge_text}
              </span>
            )}
            {copy.headline && (
              <p
                className={`text-xs font-semibold text-center pt-1 ${isDark ? 'text-amber-300/90' : 'text-amber-700'}`}
                data-testid="loyalty-ladder-headline-mobile"
              >
                {copy.headline}
              </p>
            )}
          </div>

          {/* ─── DESKTOP HEADER — pill on left, tagline on right ─── */}
          <div className="hidden sm:flex items-center justify-between mb-4 relative gap-3">
            {copy.badge_text && (
              <span
                className="px-4 py-2 rounded-2xl text-sm font-black uppercase tracking-widest bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-md shrink-0"
                data-testid="loyalty-ladder-badge"
              >
                {copy.badge_text}
              </span>
            )}
            {copy.headline && (
              <span
                className={`text-sm font-semibold text-right ${isDark ? 'text-amber-300/90' : 'text-amber-700'}`}
                data-testid="loyalty-ladder-headline"
              >
                {copy.headline}
              </span>
            )}
          </div>

          {/* ─── Milestone rail + rolling football ─── */}
          <div className="relative pt-2 pb-1" style={{ minHeight: 66 }}>
            <div className={`ll-rail-base ${isDark ? 'bg-amber-400/20' : 'bg-amber-300/50'}`} />
            <div
              className="ll-rail-fill bg-gradient-to-r from-amber-400 to-yellow-500"
              data-testid="ll-rail-fill"
            />
            <div
              className="ll-ball"
              style={{ backgroundImage: `url(${ballUrl})` }}
              aria-hidden="true"
              data-testid="ll-ball"
            />

            <div className="relative grid grid-cols-5 sm:grid-cols-6 gap-1">
              {rungs.map((r, idx) => {
                const delay = stepDelayS(idx, rungs.length);
                const hideOnMobile = idx === hideMiddleIdx;
                return (
                  <div
                    key={`${r.year_label}-${idx}`}
                    className={`flex flex-col items-center text-center ${hideOnMobile ? 'hidden sm:flex' : ''}`}
                    data-testid={`loyalty-step-${idx}`}
                    data-loyalty-cta={r.year_label}
                    onMouseEnter={r.isToday ? undefined : () => emitInteraction(r.year_label, 'hover')}
                    onClick={r.isToday ? undefined : () => emitInteraction(r.year_label, 'tap')}
                  >
                    <div className="ll-dot-wrap mb-1.5">
                      <div
                        className={`ll-dot ${r.isPeak ? 'll-dot--peak' : ''} ${r.isToday ? 'll-dot--today' : ''}`}
                        style={r.isToday ? undefined : { animationDelay: `${delay}s` }}
                        data-testid={`loyalty-dot-${idx}`}
                      />
                      {!r.isToday && (
                        <div
                          className={`ll-starburst ${r.isPeak ? 'll-starburst--peak' : ''}`}
                          style={{ animationDelay: `${delay}s` }}
                        />
                      )}
                    </div>
                    <span className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider leading-none ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {r.year_label}
                    </span>
                    <span
                      className={`ll-reveal text-base sm:text-lg font-black leading-tight mt-0.5 ${
                        r.isPeak
                          ? 'text-amber-500'
                          : r.isToday
                            ? (isDark ? 'text-gray-600' : 'text-gray-400')
                            : (isDark ? 'text-gray-500' : 'text-gray-400')
                      }`}
                      style={{ animationDelay: r.isToday ? '0s' : `${delay}s` }}
                    >
                      {r.discount_label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ─── Footer pitch ─── */}
          {(copy.footer_pitch_intro || copy.footer_pitch_emphasis || copy.footer_pitch_outro) && (
            <p
              className={`text-xs sm:text-sm mt-4 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              data-testid="loyalty-ladder-footer-pitch"
            >
              {copy.footer_pitch_intro}
              {copy.footer_pitch_emphasis && (
                <strong className={`ml-1 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  {copy.footer_pitch_emphasis}
                </strong>
              )}
              {copy.footer_pitch_outro && <> {copy.footer_pitch_outro}</>}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export default LoyaltyLadderSection;
